import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useToast } from "../../../Toaster/ToastContext";
import { useDispatch } from "react-redux";
import { setCurrentBranch } from "../../../core/redux/action";
import { apiClient } from "../../../services/apiClient";

const BranchModal = ({
  branch,
  onSave,
  canUpdate = true,
  canCreate = true,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const hasFetched = useRef(false);
  const isResetting = useRef(false);

  const resetForm = useCallback(() => {
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setStatus("active");
    setErrors({});
    console.log("resetForm: Form reset");
    hasFetched.current = false;
    isResetting.current = false;
  }, []);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Name is required";
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Valid email is required";
      // case "phone": return value.trim() ? "" : "Phone is required";
      case "phone":
        if (!value.trim()) return "Phone is required";
        if (!/^[0-9-]+$/.test(value))
          return "Phone number can only contain numbers and hyphens";
        return "";
      case "address":
        return value.trim() ? "" : "Address is required";
      default:
        return "";
    }
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {
      name: validateField("name", name),
      email: validateField("email", email),
      phone: validateField("phone", phone),
      address: validateField("address", address),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  }, [name, email, phone, address]);

  useEffect(() => {
    // console.log("useEffect: Branch prop changed", branch,hasFetched.current);

    if (branch?.id && !hasFetched.current) {
      setLoading(true);
      // console.log("useEffect: Fetching branch data for edit", branch.id);
      // editBranch(branch.id)
      apiClient
        .post("/edit-branch", { id: branch.id })
        .then(async (response) => {
          const data = await response;
          if (data.success && data.data) {
            setName(data.data.name || "");
            setEmail(data.data.email || "");
            setPhone(data.data.phone || "");
            setAddress(data.data.address || "");
            setStatus(data.data.status === "active");
            hasFetched.current = true;
            // console.log("useEffect: Branch data loaded", data.data);
          } else {
            showToast(
              data.message || "Invalid branch data",
              "error",
              "Branches"
            );
          }
        })
        .catch((error) => {
          console.error("useEffect: Error fetching branch", error);
          showToast(
            error.message || "Failed to fetch branch",
            "error",
            "Branches"
          );
        })
        .finally(() => setLoading(false));
    } else if (!branch?.id) {
      resetForm();
    }
  }, [branch?.id, showToast]);

  useEffect(() => {
    const modal = document.getElementById("branch-modal");
    if (modal) {
      const handleModalClose = () => {
        if (!isResetting.current) {
          dispatch(setCurrentBranch(null));
        }
      };
      modal.addEventListener("hidden.bs.modal", handleModalClose);
      return () =>
        modal.removeEventListener("hidden.bs.modal", handleModalClose);
    }
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      // console.log("handleSubmit: Submitting form", { name, email, phone, address, status, id: branch?.id });
      try {
        const payload = {
          name,
          email,
          phone,
          address,
          status: status ? "active" : "inactive",
        };
        if (branch?.id) payload.id = branch.id;

        // const response = branch?.id ? await updateBranch(payload) : await storeBranch(payload);
        const response = branch?.id
          ? await apiClient.post("/store-update-branch", payload)
          : await apiClient.post("/store-update-branch", payload);
        const result = await response;
        // console.log("handleSubmit: Response received", result);
        if (result.success) {
          showToast(
            result.message ||
              `Branch ${branch ? "updated" : "created"} successfully`,
            "success",
            "Branches"
          );
          resetForm();
          if (typeof onSave === "function") onSave();
          document
            .getElementById("branch-modal")
            .querySelector('[data-bs-dismiss="modal"]')
            .click();
        } else {
          showToast(
            result.message ||
              `Failed to ${branch ? "update" : "create"} branch`,
            "error",
            "Branches"
          );
        }
      } catch (error) {
        console.error("handleSubmit: Error", error);
        showToast(
          error.message || `Failed to ${branch ? "update" : "create"} branch`,
          "error",
          "Branches"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="modal fade" id="branch-modal">
      <div className="modal-dialog modal-dialog-centered custom-modal-two">
        <div className="modal-content">
          <div className="page-wrapper-new p-0">
            <div className="content">
              <div className="modal-header border-0 custom-modal-header">
                <div className="page-title">
                  <h4>{branch ? "Edit Branch" : "Add New Branch"}</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body custom-modal-body">
                <form
                  onSubmit={handleSubmit}
                  className={loading ? "form-disabled" : ""}
                >
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="input-blocks">
                        <label>Name</label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.name ? "is-invalid" : ""
                          }`}
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              name: validateField("name", e.target.value),
                            }));
                          }}
                          required
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="input-blocks">
                        <label>Email</label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              email: validateField("email", e.target.value),
                            }));
                          }}
                          required
                        />
                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="input-blocks">
                        <label>Phone</label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.phone ? "is-invalid" : ""
                          } no-spinner`}
                          value={phone}
                          onChange={(e) => {
                            setPhone(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              phone: validateField("phone", e.target.value),
                            }));
                          }}
                          required
                        />
                        {errors.phone && (
                          <div className="invalid-feedback">{errors.phone}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="input-blocks">
                        <label>Address</label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.address ? "is-invalid" : ""
                          }`}
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              address: validateField("address", e.target.value),
                            }));
                          }}
                          required
                        />
                        {errors.address && (
                          <div className="invalid-feedback">
                            {errors.address}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">Status</span>
                        <input
                          type="checkbox"
                          id="branchStatusSwitch"
                          className="check"
                          checked={status}
                          onChange={(e) => setStatus(e.target.checked)}
                        />
                        <label
                          htmlFor="branchStatusSwitch"
                          className="checktoggle"
                        ></label>
                      </div>
                    </div>
                  </div>
                  {/* <div className="modal-footer-btn">
                    <button
                      type="button"
                      className="btn btn-cancel me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-submit"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div> */}
                  <div className="modal-footer-btn">
                    <button
                      type="button"
                      className="btn btn-cancel me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>

                    {/* ✅ Create Branch button (Add mode + canCreate) */}
                    {!branch && canCreate && (
                      <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={loading}
                      >
                        {loading ? "Processing..." : "Create Branch"}
                      </button>
                    )}

                    {/* ✅ Update Branch button (Edit mode + canUpdate) */}
                    {branch && canUpdate && (
                      <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={loading}
                      >
                        {loading ? "Processing..." : "Update Branch"}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BranchModal.propTypes = {
  branch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    status: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.bool,
    ]),
  }),
  onSave: PropTypes.func,
  canUpdate: PropTypes.bool,
  canCreate: PropTypes.bool,
};
BranchModal.defaultProps = { branch: null, onSave: () => {} };

export default BranchModal;
